import { useMemo } from "react";
import { Card, Text } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { ComplianceObjectType, TaxIdType } from "~/graphql/supergraphEnums";
import EntityFormFields from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields";
import { VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EntityFields/constants";
import { BackButtonWithinForm } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/BackButtonWithinForm";
import {
  FIELD_MAPPING,
  INITIAL_VALUES,
} from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/constants";
import { getSchema } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/schema";
import { type FormValues } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/CreateEntityForm/types";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import { resolveAmlEntityId } from "~/pages/Compliance/utils/amlTypeIdUtil";
import { toISOFormat } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import { useInvestorWizard } from "../../context";
import { ExistingProfileBanner } from "../existingProfile/ExistingProfileBanner";
import { addExistingProfileTaxIdCheck } from "../existingProfile/schema";
import { TaxIdMatchingLabel } from "../existingProfile/TaxIdMatchingLabel";
import { useExistingProfileState } from "../existingProfile/useExistingProfileState";
import { ProfileFormFooter } from "../ProfileFormFooter";
import { mergeExtractedData, parseExtractedDate, toWizardAddress } from "../utils";

interface Props {
  onBack: () => void;
  extractedData?: Record<string, string> | null;
  documentId?: number;
  onViewExistingProfile?: (taxId: string) => void;
}

export const EntityProfileForm = ({
  onBack,
  extractedData,
  documentId,
  onViewExistingProfile,
}: Props) => {
  const { setNewProfileData, setProfileDraft, goNext, profileDraft, caseInfo } =
    useInvestorWizard();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const isPrefilled = Boolean(extractedData) || Boolean(documentId);
  const arenaAccessId =
    ioReusableProfilesEnabled && caseInfo?.clientId ? toDbId(caseInfo.clientId) : undefined;
  const { existingProfile, scheduleCheck } = useExistingProfileState();

  const validationSchema = useMemo(
    () =>
      addExistingProfileTaxIdCheck(getSchema(false, ioReusableProfilesEnabled), {
        arenaAccessId,
        taxIdType: TaxIdType.Ein,
        scheduleCheck,
      }),
    [arenaAccessId, ioReusableProfilesEnabled, scheduleCheck],
  );

  const draftValues =
    profileDraft?.category === ComplianceObjectType.Institution
      ? (profileDraft.values as Partial<FormValues>)
      : undefined;

  const initialValues = useMemo(() => {
    const merged = {
      ...mergeExtractedData(INITIAL_VALUES, FIELD_MAPPING, extractedData),
      ...draftValues,
    };
    return { ...merged, dateOfFormation: parseExtractedDate(merged.dateOfFormation) };
  }, [extractedData, draftValues]);

  const handleSubmit = (values: Partial<FormValues>) => {
    setProfileDraft({
      category: ComplianceObjectType.Institution,
      documentId,
      isPrefilled,
      values,
    });
    setNewProfileData({
      category: ComplianceObjectType.Institution,
      accountType: values.entityType
        ? VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE[values.entityType]
        : undefined,
      creationMethod: isPrefilled ? "document" : "manual",
      documentId,
      legalBusinessName: values.entityName,
      dateOfFormation: values.dateOfFormation ? toISOFormat(values.dateOfFormation) : undefined,
      taxId: values.taxId,
      taxIdType: TaxIdType.Ein,
      address: toWizardAddress(values),
      amlEntityId: resolveAmlEntityId(values.amlEntityType, ioReusableProfilesEnabled),
    });
    goNext();
  };

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      submitOnlyChangedValues={false}
    >
      <Card>
        <BackButtonWithinForm onClick={onBack} displayText="Create method" />
        <Text marginBottom="md" variant="h3">
          New entity
        </Text>
        {existingProfile && (
          <ExistingProfileBanner
            taxIdType={existingProfile.taxIdType}
            onViewExistingProfile={
              onViewExistingProfile
                ? () => onViewExistingProfile(existingProfile.taxId)
                : undefined
            }
          />
        )}
        <EntityFormFields
          aiExtractionSupported={isPrefilled}
          taxIdWarning={
            existingProfile ? (
              <TaxIdMatchingLabel taxIdType={existingProfile.taxIdType} />
            ) : undefined
          }
        />
      </Card>
      <ProfileFormFooter onBack={onBack} />
    </Form>
  );
};
